import {
    BoxAccess, BoxTypeSelectionResult,
    BoxUnusableDialog,
    MessageView,
    PushButton,
    useBoxes,
    useFlag,
    usePlatform,
    usePreventDeactivate
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useMemo, useState} from "react";
import {Box, LogLevel, Occupancy} from "@variocube/app-api";
import {DateTime} from "luxon";
import {SizeSelectionView} from "../SizeSelectionView";
import {BoxClose} from "../BoxClose";
import {useCubeCarrier} from "../CarrierProvider";
import {useLocalization} from "../../i18n";

interface CarrierStoreProps {
    reservation: Occupancy;
    onDone: () => void;
    onConfirm?: (occupancy: Occupancy) => void;
    preventOccupancyStoredAtUpdate?: boolean;
}

export function CarrierStore({reservation, onDone, onConfirm, preventOccupancyStoredAtUpdate}: CarrierStoreProps) {
    const platform = usePlatform();
    const boxes = useBoxes();
    const {t} = useLocalization();

    const [occupancy, setOccupancy] = useState<Occupancy>(reservation);
    const [selectSize, setSelectSize] = useState(false);
    const [sizeNotAvailable, setSizeNotAvailable] = useState(false);
    const [switchingBoxFailed, setSwitchingBoxFailed] = useState(false);
    const [boxUnusable, setBoxUnusable] = useState(false);
    const [canceled, setCanceled] = useFlag(false);
    const [switchedBox, setSwitchedBox] = useState<Box>();
    const cubeCarrier = useCubeCarrier();

    const box = useMemo(() => boxes.find(box => box.number == occupancy.boxNumber), [boxes, occupancy]);

    const handleCarrierStoreConfirm = useCallback(async () => {
        if (occupancy) {
            if (!preventOccupancyStoredAtUpdate) {
                try {
                    await platform.updateOccupancy(occupancy.uuid, {
                        storedAt: DateTime.utc().toISO(),
                    }, true);
                    await platform.log(LogLevel.Audit, "Carrier stored", {occupancy});
                } catch (error) {
                    console.error("Error updating occupancy", error);
                }
            }
        }
        onDone();
        onConfirm?.(occupancy);
    }, [occupancy, platform, onDone]);

    const handleCarrierStoreCancel = useCallback(async () => {
        if (occupancy?.content?.isAdhocDelivery) {
            await platform.endOccupancy(occupancy.uuid);
        }
        setCanceled();
    }, [occupancy, platform, setCanceled]);

    const handleOtherSize = useCallback(() => {
        setBoxUnusable(false);
        setSelectSize(true);
    }, []);
    const handleSizeBack = useCallback(() => setSelectSize(false), []);

    const handleSizeSelect = useCallback(async (typeSelectionResult: BoxTypeSelectionResult) => {
        try {
            const newOccupancy = await platform.occupy({
                ...typeSelectionResult,
                content: reservation.content,
                accessCode: reservation.accessCode,
            });
            await platform.changeAccessKeys(newOccupancy.uuid, [reservation.content.deliveryId]);
            await platform.confirmOccupancy(newOccupancy.uuid);
            await platform.endOccupancy(occupancy.uuid);
            await platform.log(LogLevel.Audit, "Carrier box change", {
                fromBox: occupancy.boxNumber,
                toBox: newOccupancy.boxNumber
            });
            setSwitchedBox(box);
            setOccupancy(newOccupancy);
        } catch (error) {
            console.error("Error switching occupancies", error);
            setSizeNotAvailable(true);
        } finally {
            setSelectSize(false);
        }
    }, [reservation, platform, occupancy, box]);

    const handleTryNextSize = useCallback(() => {
        setSizeNotAvailable(false);
        setSelectSize(true);
    }, []);

    const handleBoxNotEmpty = useCallback(async () => {
        try {
            const newOccupancyData = {
                type: box?.types[0] ?? "S",
                content: { ...reservation.content},
                accessCode: reservation.accessCode,
            };
            newOccupancyData.content.id = undefined;

            // set the current box to occupied
            occupancy.content.carrierId = "unknown";
            occupancy.content.deliveryId = "unknown";
            occupancy.content.reactivateLastOccupancy = true;
            await platform.updateOccupancy(occupancy.uuid, occupancy.content);

            // create a new occupancy
            const newOccupancy = await platform.occupy(newOccupancyData);
            await platform.changeAccessKeys(newOccupancy.uuid, [newOccupancyData.content.deliveryId]);
            await platform.confirmOccupancy(newOccupancy.uuid);

            await platform.log(LogLevel.Audit, "Carrier box change as old box is not empty", {
                fromBox: occupancy.boxNumber,
                toBox: newOccupancy.boxNumber
            });

            setSwitchedBox(box);
            setOccupancy(newOccupancy);
        } catch (error) {
            console.error("Error switching occupancies", error);
            setSwitchingBoxFailed(true);
            setCanceled();
        } finally {
            setBoxUnusable(false);
        }
    }, [reservation, platform, occupancy, box]);

    const handleBoxUnusable = useCallback(() => setBoxUnusable(true), []);
    const handleBoxUnusableClose = useCallback(() => setBoxUnusable(false), []);

    const clearSwitchedBox = useCallback(() => setSwitchedBox(undefined), []);

    usePreventDeactivate();

    if (sizeNotAvailable) {
        return (
            <MessageView message={t("BoxTypeNotAvailable")}>
                <PushButton onClick={onDone}>
                    {t("Cancel")}
                </PushButton>
                <PushButton primary onClick={handleTryNextSize}>
                    {t("SelectOtherSize")}
                </PushButton>
            </MessageView>
        );
    }

    if(switchingBoxFailed) {
        return (
            <MessageView message={t("SwitchingBoxFailed")}>
                <PushButton onClick={() => setSwitchingBoxFailed(false)}>
                    {t("Close")}
                </PushButton>
            </MessageView>
        );
    }

    if (selectSize) {
        return (
            <SizeSelectionView onBack={handleSizeBack} onSelect={handleSizeSelect}/>
        );
    }

    if (switchedBox) {
        return (
            <BoxClose box={switchedBox} onDone={clearSwitchedBox}/>
        )
    }

    if (box && canceled) {
        return (
            <BoxClose box={box} onDone={onDone}/>
        )
    }

    if (box) {
        return (
            <Fragment>
                <BoxAccess
                    boxes={box}
                    platform={platform}
                    onCancel={setCanceled}
                    disableConfirm={cubeCarrier?.firstMile?.reopenCountdown ? cubeCarrier?.firstMile?.reopenCountdown <= 1 : false}
                    onConfirm={handleCarrierStoreConfirm}
                    onBoxUnusable={handleBoxUnusable}
                    boxOpenTitle={t("StoreTitle")}
                    boxOpenMessage={t("StoreMessage")}
                    confirmTitle={t("ConfirmStoreTitle")}
                    confirmMessage={t("ConfirmStoreMessage")}
                    strings={t}
                    stopAutoConfirm={boxUnusable}
                    autoConfirm={cubeCarrier?.lastMile?.reopenCountdown}
                />
                <BoxUnusableDialog
                    open={boxUnusable}
                    onClose={handleBoxUnusableClose}
                    onWrongSize={handleOtherSize}
                    onCancel={handleCarrierStoreCancel}
                    // onNotEmpty={handleBoxNotEmpty}
                    strings={t}
                />
            </Fragment>
        );
    }
    return null;
}
